/***
*
*   DASHBOARD
*   Overview of your application usage with metrics, alerts, and activity
*
**********/

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Statistic, Select, Space, Typography,
  Tag, Timeline, Badge, Button, Progress, Empty, List, Avatar, Tooltip
} from 'antd';
import {
  TeamOutlined, ShopOutlined, UserSwitchOutlined,
  ClockCircleOutlined, CloudServerOutlined, WarningOutlined,
  ArrowUpOutlined, RightOutlined, UserAddOutlined,
  SwapOutlined, StopOutlined,
  CreditCardOutlined, ExclamationCircleOutlined,
  CalendarOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { fetchDashboardMetrics } from '../store/slices/dashboardSlice';
import { fetchAlerts } from '../store/slices/alertsSlice';
import { fetchPlans } from '../store/slices/plansSlice';
import { getCacheKey } from '../store/cacheUtils';
import { Animate } from 'components/lib';

const { Title, Text } = Typography;
const { Option } = Select;

export function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({ plan: '', status: '' });

  const dashboardState = useSelector((state) => state.dashboard);
  const alertsState = useSelector((state) => state.alerts);
  const plansState = useSelector((state) => state.plans);

  const cacheKey = getCacheKey(filters);
  const alertsCacheKey = getCacheKey({ limit: 10 });

  useEffect(() => {
    dispatch(fetchDashboardMetrics(filters));
    dispatch(fetchAlerts({ limit: 10 }));
    dispatch(fetchPlans());
  }, [dispatch, filters]);

  const data = dashboardState.data[cacheKey] || {};
  const {
    organizations = {},
    users = {},
    storage = {},
    plan_distribution = [],
    recent_activity = [],
    trial_stats = {}
  } = data;

  const alertsData = alertsState.data[alertsCacheKey] || {};
  const loading = dashboardState.loading;

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'signup': return <UserAddOutlined style={{ color: '#52c41a' }} />;
      case 'plan_change': return <SwapOutlined style={{ color: '#1890ff' }} />;
      case 'suspension': return <StopOutlined style={{ color: '#ff4d4f' }} />;
      default: return <InfoCircleOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  const getActivityLabel = (activity) => {
    switch (activity.activity_type) {
      case 'signup':
        return <Text><Text strong>{activity.name}</Text> signed up</Text>;
      case 'plan_change':
        return <Text><Text strong>{activity.name}</Text> changed plan</Text>;
      case 'suspension':
        return <Text><Text strong>{activity.name}</Text> was suspended</Text>;
      default:
        return <Text><Text strong>{activity.name}</Text> - {activity.activity_type}</Text>;
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'storage': return <CloudServerOutlined />;
      case 'trial_expiring': return <ClockCircleOutlined />;
      case 'trial_expired': return <CalendarOutlined />;
      case 'payment_failed': return <CreditCardOutlined />;
      default: return <ExclamationCircleOutlined />;
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'blue';
      default: return 'default';
    }
  };

  const storagePercent = storage.allocated > 0
    ? ((storage.used / storage.allocated) * 100).toFixed(1)
    : storage.usage_percent || 0;

  const storageStatus = storagePercent > 90 ? 'exception' : storagePercent > 70 ? 'active' : 'success';

  return (
    <Animate type="pop">
      <div style={{ padding: '0' }}>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>Dashboard</Title>
            <Text type="secondary">Overview of your application</Text>
          </div>
          <Space>
            <Select
              placeholder="All Plans"
              allowClear
              style={{ width: 200 }}
              value={filters.plan || undefined}
              onChange={(val) => handleFilterChange('plan', val || '')}
            >
              {plansState.data?.list?.map(plan => (
                <Option key={plan.value} value={plan.value}>{plan.label}</Option>
              ))}
            </Select>
            <Select
              placeholder="All Status"
              allowClear
              style={{ width: 140 }}
              value={filters.status || undefined}
              onChange={(val) => handleFilterChange('status', val || '')}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Space>
        </div>

        {/* Stats Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              hoverable
              onClick={() => navigate('/workspaces')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Total Workspaces"
                value={organizations.total || 0}
                prefix={<ShopOutlined style={{ color: '#7c3aed' }} />}
                loading={loading}
                suffix={
                  <Tooltip title={`${organizations.active || 0} active, ${organizations.inactive || 0} inactive`}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <ArrowUpOutlined /> {organizations.active || 0} active
                    </Text>
                  </Tooltip>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              hoverable
              onClick={() => navigate('/users')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Total Users"
                value={users.total || 0}
                prefix={<TeamOutlined style={{ color: '#2563eb' }} />}
                loading={loading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              hoverable
              onClick={() => navigate('/users?role=Owner')}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title="Total Owners"
                value={users.owners || 0}
                prefix={<UserSwitchOutlined style={{ color: '#059669' }} />}
                loading={loading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable onClick={() => navigate('/workspaces?plan=trial&status=active')} style={{ cursor: 'pointer' }}>
              <Statistic
                title="Active Trials"
                value={trial_stats.active_trials || 0}
                prefix={<ClockCircleOutlined style={{ color: '#d97706' }} />}
                loading={loading}
              />
            </Card>
          </Col>
        </Row>

        {/* Storage & Plan Distribution */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card title="Storage Usage" loading={loading}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Progress
                  type="dashboard"
                  percent={Number(storagePercent)}
                  status={storageStatus}
                  format={(pct) => (
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 'bold' }}>{storage.used || 0} MB</div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                        of {storage.allocated || '∞'} MB
                      </div>
                    </div>
                  )}
                />
              </div>
              {storage.over_quota_count > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <Tag color="red" icon={<WarningOutlined />}>
                    {storage.over_quota_count} organization(s) over quota
                  </Tag>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Plan Distribution" loading={loading}>
              {plan_distribution.length > 0 ? (
                <List
                  dataSource={plan_distribution}
                  renderItem={(item) => {
                    const total = plan_distribution.reduce((sum, i) => sum + i.count, 0);
                    const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                    return (
                      <List.Item>
                        <div style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <Text>{item.plan_name || item.plan}</Text>
                            <Text strong>{item.count} <Text type="secondary">({pct}%)</Text></Text>
                          </div>
                          <Progress percent={Number(pct)} showInfo={false} strokeColor="#7c3aed" size="small" />
                        </div>
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <Empty description="No plan data" />
              )}
            </Card>
          </Col>
        </Row>

        {/* Activity & Alerts */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Recent Activity" loading={loading}>
              {recent_activity.length > 0 ? (
                <Timeline
                  items={recent_activity.slice(0, 8).map((activity, idx) => ({
                    dot: getActivityIcon(activity.activity_type),
                    children: (
                      <div key={idx}>
                        {getActivityLabel(activity)}
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {new Date(activity.date).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </Text>
                        <Tag style={{ marginLeft: 8 }} color="blue">{activity.plan}</Tag>
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Empty description="No recent activity" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <span>Alerts</span>
                  {alertsData?.summary?.total > 0 && (
                    <Badge count={alertsData.summary.total} style={{ backgroundColor: '#ff4d4f' }} />
                  )}
                </Space>
              }
              extra={
                <Button type="link" onClick={() => navigate('/alerts')}>
                  See All <RightOutlined />
                </Button>
              }
              loading={alertsState.loading}
            >
              {alertsData?.alerts?.length > 0 ? (
                <List
                  dataSource={alertsData.alerts.slice(0, 6)}
                  renderItem={(alert) => (
                    <List.Item
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/workspaces/${alert.workspace_id}`)}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            icon={getAlertIcon(alert.type)}
                            style={{
                              backgroundColor: alert.severity === 'high' ? '#fff2f0' : '#fff7e6',
                              color: alert.severity === 'high' ? '#ff4d4f' : '#fa8c16'
                            }}
                          />
                        }
                        title={
                          <Space>
                            <Text>{alert.workspace_name}</Text>
                            <Tag color={getAlertColor(alert.severity)}>{alert.severity}</Tag>
                          </Space>
                        }
                        description={alert.message || alert.type}
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <Empty description="No active alerts" />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </Animate>
  );
}